import React, { useCallback, useEffect, useRef } from 'react';
import { bool, func, string } from 'prop-types';
import { Button, Checkbox, Form, Input, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import DetailForm from '@components/DetailForm';
import LanguageFormItem from '@components/LanguageFormItem';
import { loadDirectChildOrganisations } from '@redux/directChildOrganisationAdministration/actions';
import {
  selectDirectChildOrganisations,
  selectDirectChildOrganisationsState,
} from '@redux/directChildOrganisationAdministration/selectors';
import { antFormType } from '../../../propTypes';

const DirectChildOrganisationUserInviteWizard = ({
  form,
  preselectedDirectChildOrganisationSlug,
  submitting,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const organisations = useSelector(selectDirectChildOrganisations);
  const directChildOrganisationsState = useSelector(selectDirectChildOrganisationsState);
  const preselectionApplied = useRef(false);

  useEffect(() => {
    dispatch(loadDirectChildOrganisations());
  }, [dispatch]);

  useEffect(() => {
    if (
      !preselectionApplied.current &&
      directChildOrganisationsState.status === 'success' &&
      directChildOrganisationsState.slugs.includes(preselectedDirectChildOrganisationSlug)
    ) {
      preselectionApplied.current = true;
      form.setFieldsValue({ organisationSlug: preselectedDirectChildOrganisationSlug });
    }
  }, [
    directChildOrganisationsState.slugs,
    directChildOrganisationsState.status,
    form,
    preselectedDirectChildOrganisationSlug,
  ]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      form.validateFields((error, values) => {
        if (!error) onSubmit(values);
      });
    },
    [form, onSubmit],
  );

  return (
    <Card
      className="direct-child-organisation-user-form-card"
      loading={directChildOrganisationsState.status === 'loading'}
    >
      <DetailForm formProps={{ hideRequiredMark: true, onSubmit: handleSubmit }}>
        <h3>{t('directChildOrganisationAdministration:users.invite.access')}</h3>
        <Form.Item label={t('directChildOrganisationAdministration:users.invite.organisation')}>
          {form.getFieldDecorator('organisationSlug', {
            rules: [
              {
                required: true,
                message: t(
                  'directChildOrganisationAdministration:users.invite.errors.organisation',
                ),
              },
            ],
          })(
            <Select disabled={submitting}>
              {organisations.map(organisation => (
                <Select.Option key={organisation.slug} value={organisation.slug}>
                  {organisation.name}
                </Select.Option>
              ))}
            </Select>,
          )}
        </Form.Item>
        <Form.Item label={t('directChildOrganisationAdministration:users.invite.role')}>
          {form.getFieldDecorator('role', {
            rules: [
              {
                required: true,
                message: t('directChildOrganisationAdministration:users.invite.errors.role'),
              },
            ],
          })(
            <Select disabled={submitting}>
              <Select.Option value="admin">
                {t('directChildOrganisationAdministration:users.roles.admin')}
              </Select.Option>
              <Select.Option value="member">
                {t('directChildOrganisationAdministration:users.roles.member')}
              </Select.Option>
            </Select>,
          )}
        </Form.Item>
        <h3>{t('directChildOrganisationAdministration:users.invite.identity')}</h3>
        <Form.Item label={t('directChildOrganisationAdministration:users.invite.firstName')}>
          {form.getFieldDecorator('firstName', {
            rules: [
              {
                required: true,
                message: t('directChildOrganisationAdministration:users.invite.errors.firstName'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <Form.Item label={t('directChildOrganisationAdministration:users.invite.lastName')}>
          {form.getFieldDecorator('lastName', {
            rules: [
              {
                required: true,
                message: t('directChildOrganisationAdministration:users.invite.errors.lastName'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <Form.Item label={t('directChildOrganisationAdministration:users.invite.email')}>
          {form.getFieldDecorator('email', {
            rules: [
              {
                required: true,
                type: 'email',
                message: t('directChildOrganisationAdministration:users.invite.errors.email'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <LanguageFormItem currentLanguage="en" form={form} />
        <Form.Item>
          {form.getFieldDecorator('careTeamMember', {
            initialValue: false,
            valuePropName: 'checked',
          })(
            <Checkbox disabled={submitting}>
              {t('directChildOrganisationAdministration:users.invite.careTeamMember')}
            </Checkbox>,
          )}
        </Form.Item>
        <Button disabled={submitting} htmlType="submit" loading={submitting} type="primary">
          {t('directChildOrganisationAdministration:users.invite.submit')}
        </Button>
      </DetailForm>
    </Card>
  );
};

DirectChildOrganisationUserInviteWizard.propTypes = {
  form: antFormType.isRequired,
  preselectedDirectChildOrganisationSlug: string,
  submitting: bool.isRequired,
  onSubmit: func.isRequired,
};

DirectChildOrganisationUserInviteWizard.defaultProps = {
  preselectedDirectChildOrganisationSlug: undefined,
};

export default Form.create()(DirectChildOrganisationUserInviteWizard);
